import { Typography } from '@mui/material';
import { Grid, TextField, Button, FormControlLabel, Checkbox, Skeleton, Box, Stack, Switch, InputAdornment } from '@mui/material';
import MainCard from 'components/MainCard';

import * as Yup from 'yup';
import { Formik } from 'formik';

import { useSelector, useDispatch } from 'react-redux';
import * as FirestoreDataService from './../../services/FirestoreDataService';
import { showAppAlert } from 'store/reducers/appreducer';
import { useEffect, useState } from 'react';
import { setDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import OfflineDialog from 'components/OfflineDialog';

const INITIAL_VALUES = {
  tiltAlert: {
    angle_threshold: 0,
    dial: false,
    enable: false,
    time_threshold: 0,
    submit: null
  }
};

const TiltAlertSettingsPage = () => {
  const { selectedDevice, appUser } = useSelector((state) => state.appreducer);
  const [tiltAlertSettings, setTiltAlertSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const status = await FirestoreDataService.getDeviceStatus(selectedDevice);

      if (status.exists()) {
        const last_online_time = status.data()?.last_online_time;
        const offlineThreshold = 5 * 60 * 1000; // 15 minutes;
        const currentTime = Date.now();
        const deviceStatus = currentTime - last_online_time < offlineThreshold ? 'online' : 'offline';

        if (deviceStatus === 'offline') {
          setIsDialogOpen(true);
        }
      }
    })();
  }, []);

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (!selectedDevice) return;
    setLoading(true);
    (async () => {
      await FirestoreDataService.getDeviceSettings(selectedDevice)
        .then((documentSnap) => {
          if (documentSnap.exists()) {
            let tiltAlertObject = documentSnap.data().tilt_alert;
            // console.log('Fetched:-', tiltAlertObject);
            setTiltAlertSettings(tiltAlertObject);
          }
        })
        .catch((e) => {
          const msg = 'Failed to get tilt alert settings!';
          dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
          console.info(msg, e);
        })
        .finally(() => {
          setLoading(false);
        });
    })();
  }, [selectedDevice]);

  return (
    <Grid item xs={12} sm={12} md={8} lg={6}>
      <MainCard contentSX={{ p: 2.25 }} style={{ height: '100%' }}>
        <Typography variant="h6" color="textSecondary">
          Tilt Alert Settings
        </Typography>
        {loading ? (
          <>
            <Skeleton variant="rectangular" animation="wave" height={230} />
          </>
        ) : (
          <Formik
            initialValues={tiltAlertSettings ? tiltAlertSettings : INITIAL_VALUES}
            validationSchema={Yup.object().shape({
              tiltAlert: Yup.object()
                .shape({
                  angle_threshold: Yup.number()
                    .min(0, 'Angle must be greater than or equal to 0')
                    .max(90, 'Angle must be less than or equal to 90'),
                  time_threshold: Yup.number()
                    .min(10, 'Time must be greater than or equal to 10')
                    .max(3600, 'Time must be less than or equal to 3600'),
                  enable: Yup.boolean(),
                  dial: Yup.boolean()
                })
                .required()
            })}
            onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
              try {
                setStatus({ success: false });
                setSubmitting(true);

                let payload = { ...values };

                // Check permission: Try Redux appUser first, fallback to sessionStorage for partner sessions
                let canModifySettings = appUser?.organization?.extra_info?.can_user_modify_settings;
                if (!canModifySettings) {
                  const isPartnerSession = typeof window !== 'undefined' && sessionStorage.getItem('is_partner_session') === 'true';
                  if (isPartnerSession) {
                    try {
                      const userDataStr = sessionStorage.getItem('user');
                      if (userDataStr) {
                        const partnerUser = JSON.parse(userDataStr);
                        canModifySettings = partnerUser?.organization?.extra_info?.can_user_modify_settings;
                      }
                    } catch (e) {
                      console.error('Failed to parse partner user data from sessionStorage:', e);
                    }
                  }
                }
                if (!canModifySettings) {
                  console.log('cant save settings');
                  dispatch(
                    showAppAlert({
                      show: true,
                      alertType: 'error',
                      alertMessage: 'Error: You do not have permission to save these settings.'
                    })
                  );
                  return;
                }

                // console.log('payload onSubmit: ', payload);

                if (values.angle_threshold < 0) {
                  payload = { ...values, angle_threshold: 0 };
                }

                if (values.angle_threshold > 90) {
                  payload = { ...values, angle_threshold: 90 };
                }

                if (values.time_threshold < 10) {
                  payload = { ...values, time_threshold: 10 };
                }

                if (values.time_threshold > 3600) {
                  payload = { ...values, time_threshold: 3600 };
                }

                const tasksRef = doc(db, 'tasks', selectedDevice);
                setDoc(tasksRef, {
                  device_id: selectedDevice,
                  type: 'tilt_alert',
                  user_id: appUser.email ? appUser.email : '-',
                  old_value: tiltAlertSettings,
                  payload
                })
                  .then((response) => {
                    // console.log('saved tasks!', response);
                    const msg = 'Tilt alert settings saved successfully !';
                    dispatch(showAppAlert({ show: true, alertType: 'success', alertMessage: msg }));
                  })
                  .catch((e) => {
                    const msg = 'Failed to save tilt alert settings!';
                    console.log(msg, e);
                    dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: msg }));
                  });
              } catch (err) {
                dispatch(showAppAlert({ show: true, alertType: 'error', alertMessage: 'Failed to save tilt alert settings' }));
                setStatus({ success: false });
                setErrors({ submit: err.message });
                setSubmitting(false);
              }
            }}
          >
            {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Box sx={{ mt: 2 }}>
                  <Grid item xs={12} justifyContent="space-between" alignItems="center" sx={{ display: 'flex' }}>
                    <Typography variant="caption" color="textSecondary">
                      Threshold Settings for Tilt Alert
                    </Typography>
                    <FormControlLabel
                      name="enable"
                      id="enable"
                      control={<Switch onChange={handleChange} checked={values.enable} />}
                      label="Enable"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Stack spacing={1} style={{ marginTop: '16px' }}>
                      <TextField
                        disabled={!values.enable}
                        fullWidth
                        id="time_threshold"
                        type="number"
                        value={values.time_threshold}
                        name="time_threshold"
                        onChange={handleChange}
                        placeholder="10s - 3600s Unit Seconds."
                        label="Time Threshold"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">seconds</InputAdornment>
                        }}
                      />
                    </Stack>
                    <Stack spacing={1} style={{ marginTop: '24px' }}>
                      <TextField
                        disabled={!values.enable}
                        fullWidth
                        id="angle_threshold"
                        type="number"
                        value={values.angle_threshold}
                        name="angle_threshold"
                        onChange={handleChange}
                        placeholder="Angle: 0 - 90 degree."
                        label="Angle Threshold"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">degrees</InputAdornment>
                        }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={12} style={{ paddingTop: 8 }}>
                    <FormControlLabel
                      disabled={!values.enable}
                      name="dial"
                      id="dial"
                      control={<Checkbox onChange={handleChange} checked={values.dial} />}
                      label="Recieve Call"
                    />
                    <Button disableElevation style={{ float: 'right' }} type="submit" variant="contained" color="primary">
                      Save
                    </Button>
                  </Grid>
                </Box>
              </form>
            )}
          </Formik>
        )}
      </MainCard>
      <OfflineDialog isOpen={isDialogOpen} handleClose={handleDialogClose}></OfflineDialog>
    </Grid>
  );
};

export default TiltAlertSettingsPage;
